import { EditableStateInfo } from 'types/story';
import { templateString } from 'common/utils/template-string';
import { FIELD_BB_ERROR_STYLE, FIELD_BB_STYLE } from 'client/components/common/BuildingBlocks/Fields/constants';

export { IS_DEVEL } from 'common/utils/environment';
export const ADMIN_SESSION = 'adminSessionId';
export const CLIENT_SESSION = 'sessionId';

export const CARD_TYPE = {
	INFO: 'INFO',
	TRIVIA: 'TRIVIA',
	POLL: 'POLL',
	PERSONALITY_TEST: 'PERSONALITY_TEST',
	TRUE_OR_FALSE: 'TRUE_OR_FALSE',
	THIS_OR_THAT: 'THIS_OR_THAT',
	NAVIGATION: 'NAVIGATION',
	FORM: 'FORM',
	REGISTRATION: 'REGISTRATION',
	SANDBOX: 'SANDBOX',
} as const;

export const PARAMS = {
	ORGANIZATION_ID: 'organizationId',
	DOMAIN_ID: 'domainId',
	STORY_ID: 'storyId',
	CARD_ID: 'cardId',
	TEAM_ID: 'teamId',
	MEMBER_ID: 'memberId',
	TEMPLATE_ID: 'templateId',
	PROVIDER: 'provider',
	REDIRECT: 'redirect', // '0' | undefined
} as const;

export const COMPONENT_TYPE = {
	TEXT: 'TEXT',
	IMG: 'IMG',
	ANSWER: 'ANSWER',
	FLOAT_ABOVE: 'FLOAT_ABOVE',
	FLOAT_BELOW: 'FLOAT_BELOW',
	CONTENT: 'CONTENT',
	CARD: 'CARD',
	BUTTON: 'BUTTON',
	BUTTON_SUBMIT: 'BUTTON_SUBMIT',
	GOOGLE_LOGIN: 'GOOGLE_LOGIN',
	SMS_LOGIN: 'SMS_LOGIN',
	BOX: 'BOX',
	SHAPE: 'SHAPE',
	EMBED: 'EMBED',
	TEXT_FIELD: 'TEXT_FIELD',
	TEXT_AREA_FIELD: 'TEXT_AREA_FIELD',
	CHECKBOX: 'CHECKBOX',
	SHARE: 'SHARE',
	RESULT_TEXT: 'RESULT_TEXT',
	RESULT_SHAPE: 'RESULT_SHAPE',
	SWIPE: 'SWIPE',
	VIDEO: 'VIDEO',
	TIMER: 'TIMER',
} as const;

export const STORY_SETTINGS_COMPONENTS = {
	[COMPONENT_TYPE.CARD]: {
		path: templateString('cards.{{ cardId }}'),
	},
	[COMPONENT_TYPE.ANSWER]: {
		path: templateString('cards.{{ cardId }}.answers.{{ answerId }}'),
	},
};

export const CARD_ROOT_ELEMENT_INDEX = {
	[COMPONENT_TYPE.FLOAT_ABOVE]: 0,
	[COMPONENT_TYPE.CONTENT]: 1,
	[COMPONENT_TYPE.FLOAT_BELOW]: 2,
	[COMPONENT_TYPE.CARD]: 3,
} as const;

export const STORY_ROOT_ELEMENT_INDEX = {
	[COMPONENT_TYPE.FLOAT_ABOVE]: 0,
	[COMPONENT_TYPE.FLOAT_BELOW]: 1,
};

export const LAYER_TYPE = {
	// this layer adds block to story.elements and leave his original ref in card element without render.
	// Also wraps it with a fixed positioned container
	FIXED_GLOBAL: 0,
	FIXED: 1, // this layer wraps block with a fixed positioned container
	FLOAT: 2, // this layer moves block to one of the float layers
	CONTENT: undefined, // this layer moves block to "content" container at card elements
} as const;

export const IFRAME_ACTIONS = {
	GET_STORY: '@IFRAME/GET_STORY',
	UPDATE_ADMIN_PREVIEW_LOCATION: '@IFRAME/UPDATE_ADMIN_LOCATION',
	UPDATE_CARD_ELEMENT_BY_PATH: '@IFRAME/UPDATE_CARD_ELEMENT_BY_PATH',
	UPDATE_STORY_ELEMENT_BY_PATH: '@IFRAME/UPDATE_STORY_ELEMENT_BY_PATH',
	SET_EDITABLE_EDITOR_ELEMENT: '@IFRAME/SET_EDITABLE_EDITOR_ELEMENT',
	INITIATE_SELECT_EDITOR_ELEMENT: '@IFRAME/INITIATE_SELECT_EDITOR_ELEMENT',
	APPLY_SCROLLBAR_FIX: '@IFRAME/APPLY_SCROLLBAR_FIX',
	UPDATE_CLIENT_CARD_DATA: '@EDITOR/UPDATE_CLIENT_CARD_DATA',
	UPDATE_CLIENT_STORY_ELEMENTS: '@EDITOR/UPDATE_CLIENT_STORY_ELEMENTS',
};

export const DEFAULT_MEDIA_QUERY_PLATFORMS = {
	DEFAULT: 'default',
	FULL_HD: 'fullHD',
	DESKTOP: 'desktop',
	TABLET: 'tablet',
	MOBILE_LANDSCAPE: 'mobileLandscape',
	MOBILE: 'mobile',
} as const;

export const MQ_SIZES_TEXT_MAP = {
	[DEFAULT_MEDIA_QUERY_PLATFORMS.FULL_HD]: 'createStoryModal.platform.full_hd.mq',
	[DEFAULT_MEDIA_QUERY_PLATFORMS.DESKTOP]: 'createStoryModal.platform.desktop.mq',
	[DEFAULT_MEDIA_QUERY_PLATFORMS.TABLET]: 'createStoryModal.platform.tablet.mq',
	[DEFAULT_MEDIA_QUERY_PLATFORMS.MOBILE_LANDSCAPE]: 'createStoryModal.platform.mobile_landscape.mq',
	[DEFAULT_MEDIA_QUERY_PLATFORMS.MOBILE]: 'createStoryModal.platform.mobile.mq',
};

export const DEFAULT_MEDIA_QUERY_SIZES = {
	[DEFAULT_MEDIA_QUERY_PLATFORMS.FULL_HD]: { min: 1920, ratio: 1920 / 1080 },
	[DEFAULT_MEDIA_QUERY_PLATFORMS.DESKTOP]: { min: 1280, ratio: 1280 / 800 },
	[DEFAULT_MEDIA_QUERY_PLATFORMS.TABLET]: { min: 768, ratio: 1024 / 768 },
	[DEFAULT_MEDIA_QUERY_PLATFORMS.MOBILE_LANDSCAPE]: { min: 479, ratio: 667 / 375 },
	[DEFAULT_MEDIA_QUERY_PLATFORMS.MOBILE]: { min: null, ratio: 375 / 667 },
};

// Supported states by app
export const COMPONENT_STATES = {
	DEFAULT: 'defaultState',
	// [data-selected-state]
	SELECTED: 'selectedState',
	// [data-hover-state]
	HOVER: 'hoverState',
	// [data-selected-hover-state]
	SELECTED_HOVER: 'selectedHoverState',
	// [data-correct-state]
	CORRECT: 'correctState',
	// [data-incorrect-state]
	INCORRECT: 'incorrectState',
} as const;

// List of states in order of assignment
export const COMPONENT_STATES_ORDERED = [
	COMPONENT_STATES.DEFAULT,
	COMPONENT_STATES.SELECTED,
	COMPONENT_STATES.HOVER,
	COMPONENT_STATES.SELECTED_HOVER,
	COMPONENT_STATES.CORRECT,
	COMPONENT_STATES.INCORRECT,
];

// List of states used by particular building blocks
export const COMPONENT_STATES_LIST = {
	[COMPONENT_TYPE.ANSWER]: [
		COMPONENT_STATES.DEFAULT,
		COMPONENT_STATES.SELECTED,
		COMPONENT_STATES.CORRECT,
		COMPONENT_STATES.INCORRECT,
		COMPONENT_STATES.HOVER,
	],
	[COMPONENT_TYPE.BUTTON]: [COMPONENT_STATES.DEFAULT, COMPONENT_STATES.HOVER],
	[COMPONENT_TYPE.BUTTON_SUBMIT]: [COMPONENT_STATES.DEFAULT, COMPONENT_STATES.HOVER],
	[COMPONENT_TYPE.IMG]: [COMPONENT_STATES.DEFAULT, COMPONENT_STATES.HOVER],
	[COMPONENT_TYPE.SHAPE]: [COMPONENT_STATES.DEFAULT, COMPONENT_STATES.HOVER],
	[COMPONENT_TYPE.TEXT_FIELD]: [COMPONENT_STATES.DEFAULT, COMPONENT_STATES.INCORRECT],
	[COMPONENT_TYPE.TEXT_AREA_FIELD]: [COMPONENT_STATES.DEFAULT, COMPONENT_STATES.INCORRECT],
	[COMPONENT_TYPE.CHECKBOX]: [
		COMPONENT_STATES.DEFAULT,
		COMPONENT_STATES.HOVER,
		COMPONENT_STATES.SELECTED,
		COMPONENT_STATES.INCORRECT,
	],
	[COMPONENT_TYPE.SHARE]: [COMPONENT_STATES.DEFAULT, COMPONENT_STATES.HOVER],
	[COMPONENT_TYPE.SWIPE]: [COMPONENT_STATES.DEFAULT, COMPONENT_STATES.SELECTED],
};

export const DEFAULT_SCORE_LIMIT = {
	MIN: 0, // total score min
	MAX: 100, // total score max
	RANGE: 10, // min range size
};

export const DEFAULT_SCORE = 5; // 5 is a default value at backend

export const FLOW_DEFAULT_EVENT_NAME = 'Default';

// Flow event connection type
export const CONNECTION_TYPES = {
	NEXT: 'NEXT', // bind to 1st card in next step
	ANY: 'ANY', // bind to point by end point id (card)
	STEP: 'STEP', // bind to point by end point id (step) (random card in step)
} as const;

export const CUSTOM_CSS = '__CSS' as const;
export const SHAPE_LINE_THICKNESS = '__shapeLineThickness';
export const GLOBAL_BLOCK_DISPLAY = '__globalBlockDisplay';
export const PIN = {
	l: 'l',
	t: 't',
	r: 'r',
	b: 'b',
	c: 'c',
	lt: 'lt',
	lb: 'lb',
	rb: 'rb',
	rt: 'rt',
	none: '',
} as const;
export const PIN_DEFAULT = PIN.lt;
export const PIN_TYPE = 'pinType' as const;
export const CARD_TINT = 'cardTint';
export const IMG_TINT = 'imgTint';
export const IMG_TINT_ATTR = 'data-img-tint';
export const CONTENT_ALIGN_Y = {
	top: 'top',
	center: 'center',
	bottom: 'bottom',
} as const;

/*
 These are the properties which placed in building block at "componentProps|editorProps"
 path and they are NOT depends on state or media query and also without inheritance. Assigned once for all cases.
 */
export const UNIFORM_PROPS = {
	shareType: 'shareType',
	shareCard: 'shareCard',
	answerTimeout: 'answerTimeout',
	btnLink: 'btnLink',
	btnNavLink: 'btnNavLink',
	btnTarget: 'btnTarget',
	btnSubmit: 'btnSubmit',
	fieldPlaceholder: 'fieldPlaceholder',
	fieldRequired: 'fieldRequired',
	fieldType: 'fieldType', // one of FIELD_BB_TYPE
	fieldError: 'fieldError', // one of FIELD_BB_ERROR_TYPE
	fieldErrorShow: 'fieldErrorShow',
	fieldPlaceholderShow: 'fieldPlaceholderShow',
	onSwipe: 'onSwipe',
	tag: 'tag',
	title: 'title',
	timerTime: 'timerTime',
	timerType: 'timerType',
	expose: 'expose',
	duration: 'duration', // expose.duration
	effect: 'effect', // expose.effect
} as const;

export const TRANSITION_TYPE = {
	NONE: 'none',
	FADE: 'fade',
	ZOOM: 'zoom',
} as const;

export const EXPOSE_DEFAULT_DURATION = 1.5; // seconds

export const ANIMATION_STATE = {
	NONE: 'NONE',
	NOT_STARTED: 'NOT_STARTED',
	PLAYING: 'PLAYING',
	COMPLETE: 'COMPLETE',
} as const;

export const ANIMATION_TRIGGER = {
	ON_LOAD: 'onLoad',
	ON_CARD_EXPOSE_START: 'onCardExposeStart',
	ON_CARD_EXPOSE_COMPLETE: 'onCardExposeComplete',
	ON_VIEWPORT: 'onViewport',
	ON_HOVER: 'onMouseEnter',
	ON_CLICK: 'onClick',
} as const;

export const ANIMATION_KEYFRAMES = {
	INITIAL: 'initial',
	FINAL: 'final',
} as const;

export const EDITABLE_COMPONENT_STATE_MODEL: EditableStateInfo = {
	state: COMPONENT_STATES.DEFAULT,
	source: {
		id: '',
		path: '',
		type: '',
		layer: LAYER_TYPE.CONTENT,
	},
} as const;

export const HAS_ANSWERS_ATTR = 'data-has-answers';

/**
 * List of supported css properties, divided into conditional groups
 */
export const CSS_PROPS = {
	grid: {
		gridTemplateRows: 'gridTemplateRows',
		gridTemplateColumns: 'gridTemplateColumns',
		gridRowGap: 'gridRowGap',
		gridColumnGap: 'gridColumnGap',
		gridAutoRows: 'gridAutoRows',
		gridAutoColumns: 'gridAutoColumns',
		gridRow: 'gridRow',
		gridColumn: 'gridColumn',
	},
	layout: {
		display: 'display',
		marginLeft: 'marginLeft',
		marginRight: 'marginRight',
		marginTop: 'marginTop',
		marginBottom: 'marginBottom',
		paddingLeft: 'paddingLeft',
		paddingRight: 'paddingRight',
		paddingTop: 'paddingTop',
		paddingBottom: 'paddingBottom',
		flex: 'flex',
		flexDirection: 'flexDirection',
		flexWrap: 'flexWrap',
		flexGrow: 'flexGrow',
		flexShrink: 'flexShrink',
		flexBasis: 'flexBasis',
		alignItems: 'alignItems',
		justifyContent: 'justifyContent',
	},
	size: {
		width: 'width',
		height: 'height',
		minWidth: 'minWidth',
		minHeight: 'minHeight',
		maxWidth: 'maxWidth',
		maxHeight: 'maxHeight',
		overflow: 'overflow',
	},
	position: {
		position: 'position',
		left: 'left',
		right: 'right',
		top: 'top',
		bottom: 'bottom',
		zIndex: 'zIndex',
		float: 'float',
		clear: 'clear',
	},
	text: {
		fontFamily: 'fontFamily',
		fontWeight: 'fontWeight',
		fontSize: 'fontSize',
		color: 'color',
		textAlign: 'textAlign',
		lineHeight: 'lineHeight',
		letterSpacing: 'letterSpacing',
		direction: 'direction',
		textTransform: 'textTransform',
		textShadow: 'textShadow',
	},
	background: {
		backgroundColor: 'backgroundColor',
		backgroundImage: 'backgroundImage',
		backgroundPosition: 'backgroundPosition',
		backgroundRepeat: 'backgroundRepeat',
		backgroundSize: 'backgroundSize',
		objectFit: 'objectFit',
		objectPosition: 'objectPosition',
	},
	borders: {
		borderWidth: 'borderWidth',
		borderStyle: 'borderStyle',
		borderColor: 'borderColor',
		borderRadius: 'borderRadius',
		borderTopRightRadius: 'borderTopRightRadius',
		borderTopLeftRadius: 'borderTopLeftRadius',
		borderBottomLeftRadius: 'borderBottomLeftRadius',
		borderBottomRightRadius: 'borderBottomRightRadius',
	},
	effects: {
		opacity: 'opacity',
		boxShadow: 'boxShadow',
		// transform: 'transform',
		// transition: 'transition',
		filter: 'filter',
		cursor: 'cursor',
		pointerEvents: 'pointerEvents',
	},
	custom: {
		[CUSTOM_CSS]: CUSTOM_CSS,
	},
	color: {
		color: 'color',
		backgroundColor: 'backgroundColor',
	},
	shape: {
		lineThickness: SHAPE_LINE_THICKNESS,
	},
	field: {
		style: FIELD_BB_STYLE,
		errorStyle: FIELD_BB_ERROR_STYLE,
	},
	image: {
		tint: IMG_TINT,
	},
	timer: {
		strokeWidth: 'strokeWidth',
		trackColor: 'trackColor',
		progressColor: 'progressColor',
	},
} as const;

export const STORY_SETTING_PROP = {
	showResults: 'showResults',
	showCorrect: 'showCorrect',
	score: 'score',
} as const;

export const ELEMENT_EDIT_MODE = {
	INSTANCE: 'INSTANCE', // edit current element (default)
	MASTER: 'MASTER', // edit master symbol linked to current element (Symbols only)
} as const;

export const CARD_DEFAULT_TEMPLATE_NAME = 'DEFAULT';

export const STORY_TYPE = {
	STANDALONE: 'standalone',
	EMBED: 'embed',
} as const;
